import React from "react";
import { fmtDaysUntil, fmtRelativeTime } from "./formatters";

// ----- Status pill: active / quiet / silent / new ---------------------
const STATUS_PILL = {
  active: { bg: "#e8f8e9", fg: "#1b6b1e", label: "active" },
  quiet:  { bg: "#fff8e7", fg: "#8a5a00", label: "quiet" },
  silent: { bg: "#fdecec", fg: "#a23a3a", label: "silent" },
  new:    { bg: "#e7f3ff", fg: "#004c80", label: "new" },
};

function StatusPill({ status }) {
  const cfg = STATUS_PILL[status] || STATUS_PILL.quiet;
  return (
    <span
      style={{
        display: "inline-block",
        background: cfg.bg,
        color: cfg.fg,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {cfg.label}
    </span>
  );
}

function RatingCell({ value, count }) {
  if (value == null) {
    return <span className="muted" style={{ fontSize: "0.85rem" }}>—</span>;
  }
  const tone =
    value >= 4.0 ? "#1b6b1e" :
    value >= 3.5 ? "#8a5a00" : "#a23a3a";
  return (
    <span style={{ color: tone, fontWeight: 600, fontSize: "0.88rem" }}>
      ★ {Number(value).toFixed(1)}
      <span className="muted" style={{ fontWeight: 400, fontSize: "0.75rem" }}>
        {" "}({count || 0})
      </span>
    </span>
  );
}

function OrdersTodayCell({ today, yesterday }) {
  const today_n = today || 0;
  const y_n = yesterday || 0;
  let delta = null;
  let tone = "#5c7c7f";
  if (y_n > 0) {
    const change = today_n - y_n;
    if (change > 0) { delta = `+${change}`; tone = "#1b6b1e"; }
    else if (change < 0) { delta = `${change}`; tone = "#a23a3a"; }
    else { delta = "±0"; }
  } else if (today_n > 0) {
    delta = "first today";
    tone = "#1b6b1e";
  }
  return (
    <span style={{ fontSize: "0.88rem" }}>
      <b>{today_n}</b>
      <span className="muted"> / {y_n}</span>
      {delta && (
        <div style={{ fontSize: "0.72rem", color: tone, marginTop: 1 }}>{delta}</div>
      )}
    </span>
  );
}

// Tenants health table card: search / tier / sort controls, rows and
// pagination. All filter state, data fetching and the impersonation
// handler live in AdminPage and arrive via props.
export default function TenantTable({
  tenants,
  loading,
  q,
  setQ,
  tier,
  setTier,
  sort,
  setSort,
  page,
  setPage,
  totalPages,
  onView,
  onLoginAs,
  impersonatingId,
}) {
  return (
    <div className="card" style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 12,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0, marginRight: "auto" }}>Tenants</h3>
        <input
          placeholder="Search name or email…"
          value={q}
          onChange={(e) => { setPage(0); setQ(e.target.value); }}
          style={{ minWidth: 200 }}
        />
        <select
          value={tier}
          onChange={(e) => { setPage(0); setTier(e.target.value); }}
          title="Filter by tier"
        >
          <option value="">All tiers</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
          <option value="business">Business</option>
        </select>
        <select
          value={sort}
          onChange={(e) => { setPage(0); setSort(e.target.value); }}
          title="Sort"
        >
          <option value="needs_attention">Needs attention first</option>
          <option value="name">Name (A→Z)</option>
          <option value="joined">Recently joined</option>
          <option value="orders_today">Most orders today</option>
          <option value="rating">Lowest rating first</option>
          <option value="last_activity">Most recent activity</option>
        </select>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : tenants.items.length === 0 ? (
        <p className="muted">No tenants match.</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "1px solid var(--border)", fontSize: "0.78rem", textTransform: "uppercase", letterSpacing: 0.5, color: "#5c7c7f" }}>
                <th style={{ padding: "8px 6px" }}>Status</th>
                <th style={{ padding: "8px 6px" }}>Restaurant</th>
                <th style={{ padding: "8px 6px" }}>Tier</th>
                <th style={{ padding: "8px 6px" }} title="Today vs yesterday">Orders today</th>
                <th style={{ padding: "8px 6px" }}>Last order</th>
                <th style={{ padding: "8px 6px" }} title="Food rating · last 7d">Food 7d</th>
                <th style={{ padding: "8px 6px" }} title="Service rating · last 7d">Service 7d</th>
                <th style={{ padding: "8px 6px" }} title="Open tabs right now">Open tabs</th>
                <th style={{ padding: "8px 6px" }}>Pilot ends</th>
                <th style={{ padding: "8px 6px", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.items.map((t) => {
                const pilotEnds = fmtDaysUntil(t.pilot_ends_at);
                return (
                  <tr
                    key={t.id}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      background: t.status === "silent" ? "rgba(253, 236, 236, 0.4)" : undefined,
                    }}
                  >
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <StatusPill status={t.status} />
                      {t.is_churn_risk && (
                        <div
                          style={{
                            fontSize: "0.66rem",
                            color: "#a23a3a",
                            marginTop: 4,
                            fontWeight: 700,
                          }}
                          title="Stripe subscriber with no orders in 14 days"
                        >
                          CHURN RISK
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <div style={{ fontWeight: 600 }}>{t.name}</div>
                      <div className="muted" style={{ fontSize: "0.75rem" }}>
                        #{t.id} · {t.email}
                      </div>
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <span className={`tier-badge tier-${t.subscription_tier}`}>
                        {t.subscription_tier}
                      </span>
                      {t.has_subscription && (
                        <div className="muted" style={{ fontSize: "0.7rem", marginTop: 2 }}>
                          stripe ✓
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <OrdersTodayCell today={t.orders_today} yesterday={t.orders_yesterday} />
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <span style={{ fontSize: "0.85rem" }}>
                        {fmtRelativeTime(t.last_order_at)}
                      </span>
                      {t.orders_30d > 0 && (
                        <div className="muted" style={{ fontSize: "0.7rem" }}>
                          {t.orders_30d} in 30d
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <RatingCell value={t.avg_food_rating_7d} count={t.rating_count_7d} />
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      <RatingCell value={t.avg_service_rating_7d} count={t.rating_count_7d} />
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top", textAlign: "center" }}>
                      {t.open_tabs_count > 0 ? (
                        <span style={{ fontWeight: 600 }}>{t.open_tabs_count}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top" }}>
                      {pilotEnds ? (
                        <span
                          style={{
                            fontSize: "0.78rem",
                            color: pilotEnds === "tomorrow" || pilotEnds === "ended" ? "#a23a3a" : "#8a5a00",
                            fontWeight: 600,
                          }}
                        >
                          {pilotEnds}
                        </span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td style={{ padding: "10px 6px", verticalAlign: "top", textAlign: "right", whiteSpace: "nowrap" }}>
                      <button
                        className="btn secondary small"
                        onClick={() => onView(t)}
                        title="Open full detail page"
                        style={{ marginRight: 4 }}
                      >
                        View
                      </button>
                      <button
                        className="btn small"
                        onClick={() => onLoginAs(t)}
                        disabled={impersonatingId === t.id}
                        title="Switch to this tenant's manager view"
                      >
                        {impersonatingId === t.id ? "Switching…" : "Login as"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          marginTop: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <span className="muted" style={{ fontSize: "0.85rem" }}>
          {tenants.total} total · page {page + 1} of {totalPages} · sorted by {sort.replace(/_/g, " ")}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn secondary small"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            ← Prev
          </button>
          <button
            className="btn secondary small"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
